import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LinearUnit } from '@trimble-gcs/common';
import {
  ModusButtonModule,
  ModusFormFieldModule,
  ModusIconModule,
  ModusInputModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { filter, map } from 'rxjs';
import { DEFAULT_SCAN_3D_STYLE, Scan3dStyle } from '../../models/scan-3d-style';
import { Scan3dStyleUom } from '../../models/scan-3d-style-uom';
import { Scan3dLengthInputComponent } from '../scan-3d-length-input/scan-3d-length-input.component';
import { Scan3dLengthInputDirective } from '../scan-3d-length-input/scan-3d-length-input.directive';
import { Scan3dUomLinearConverter } from '../scan-3d-uom-linear-converter';
import { getElevationGradient, rgbToHex } from './scan-3d-elevation-utils';
import { scan3dElevationTranslations } from './scan-3d-elevation.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-elevation',
  imports: [
    ReactiveFormsModule,
    ModusFormFieldModule,
    ModusButtonModule,
    ModusInputModule,
    ModusIconModule,
    ModusTooltipModule,
    Scan3dLengthInputComponent,
    Scan3dLengthInputDirective,
    MatSliderModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-elevation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dElevationComponent {
  readonly elevationPicking = input<boolean>(false);
  readonly scan3dStyle = input.required<Scan3dStyle>();
  readonly scan3dStyleUom = input.required<Scan3dStyleUom>();

  readonly styleChange = output<Partial<Scan3dStyle>>();
  readonly elevationPickClick = output<void>();

  readonly lengthUnit = computed(() => this.scan3dStyleUom().lengthUnit);

  readonly elevationPickingTooltip = computed(() =>
    this.elevationPicking()
      ? this.translations.reference.deactivatePicking.text
      : this.translations.reference.activatePicking.text,
  );

  readonly toleranceText = computed(() => {
    const uom = this.scan3dStyleUom();
    const style = this.scan3dStyle();
    return this.formatValueWithUnit(style.elevationTolerance, uom);
  });

  readonly referenceText = computed(() => {
    const uom = this.scan3dStyleUom();
    const style = this.scan3dStyle();
    const reference = style.elevationReference + style.elevationOffset;
    return this.formatValueWithUnit(reference, uom);
  });

  readonly elevationGradient = computed(() => {
    const style = this.scan3dStyle();
    // An extra band is needed to render the out of tolerance color
    const bandCount = style.elevationBandCount + 1;
    return getElevationGradient(bandCount).map(([, color]) => rgbToHex(color));
  });

  readonly middleGradientIndex = computed(() => Math.floor(this.elevationGradient().length / 2));

  readonly translations = scan3dElevationTranslations();

  readonly rgbToHex = rgbToHex;

  private readonly numberPattern = /^-?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/; // Matches integers, decimals (including .[0-9]), negatives and scientific notation

  private readonly uomConverter = new Scan3dUomLinearConverter();

  readonly formGroup = new FormGroup({
    reference: new FormControl<number>(DEFAULT_SCAN_3D_STYLE.elevationReference, {
      validators: [Validators.required, Validators.pattern(this.numberPattern)],
    }),
    tolerance: new FormControl<number>(DEFAULT_SCAN_3D_STYLE.elevationTolerance, {
      validators: [Validators.required, Validators.min(0), Validators.pattern(this.numberPattern)],
    }),
    offset: new FormControl<number>(DEFAULT_SCAN_3D_STYLE.elevationOffset, {
      validators: [Validators.required, Validators.pattern(this.numberPattern)],
    }),
    bandCount: new FormControl<number>(DEFAULT_SCAN_3D_STYLE.elevationBandCount),
  });

  get errorReference(): boolean {
    const control = this.formGroup.controls.reference;
    return !control.valid && !!control.touched;
  }

  get errorTolerance(): boolean {
    const control = this.formGroup.controls.tolerance;
    return !control.valid && !!control.touched;
  }

  get errorOffset(): boolean {
    const control = this.formGroup.controls.offset;
    return !control.valid && !!control.touched;
  }

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToFormValueChanges();
  }

  onElevationPickClick(): void {
    this.elevationPickClick.emit();
  }

  private subscribeToScan3dStyle(): void {
    toObservable(this.scan3dStyle)
      .pipe(
        // Only reset the form if the input style has changed from the form values.
        filter((style) => !this.scan3dStylesEqual(style, this.getFormValue())),
        untilDestroyed(this),
      )
      .subscribe((style) => {
        this.formGroup.reset(
          {
            reference: style.elevationReference,
            tolerance: style.elevationTolerance,
            offset: style.elevationOffset,
            bandCount: style.elevationBandCount,
          },
          { emitEvent: false },
        );
      });
  }

  private subscribeToFormValueChanges(): void {
    this.formGroup.valueChanges
      .pipe(
        filter(() => this.formGroup.valid),
        map(() => this.getFormValue()),
        // Only emit if the form has changed from the input style values.
        // Prevents unnecessary emits for equivalent changes (e.g. 0 vs 0.0).
        filter((style) => !this.scan3dStylesEqual(style, this.scan3dStyle())),
        untilDestroyed(this),
      )
      .subscribe((partialStyle) => {
        this.styleChange.emit(partialStyle);
      });
  }

  private formatValueWithUnit(value: number, uom: Scan3dStyleUom): string {
    if (uom.lengthUnit === LinearUnit.FeetInch) {
      const { feet, inches, fraction } = this.uomConverter.toFeetInchesFraction(value, uom);
      return `${feet} ft ${inches} ${fraction}/${uom.lengthFractions} in`;
    } else {
      const convertedValue = this.uomConverter.toUom(value, uom);
      const formattedValue = convertedValue.toFixed(uom.lengthDecimals);
      return `${formattedValue} ${this.lengthUnit()}`;
    }
  }

  private getFormValue(): Partial<Scan3dStyle> {
    const value = this.formGroup.value;

    return {
      elevationReference: value.reference ?? DEFAULT_SCAN_3D_STYLE.elevationReference,
      elevationTolerance: value.tolerance ?? DEFAULT_SCAN_3D_STYLE.elevationTolerance,
      elevationOffset: value.offset ?? DEFAULT_SCAN_3D_STYLE.elevationOffset,
      elevationBandCount: value.bandCount ?? DEFAULT_SCAN_3D_STYLE.elevationBandCount,
    };
  }

  private scan3dStylesEqual(a: Partial<Scan3dStyle>, b: Partial<Scan3dStyle>): boolean {
    return (
      a.elevationReference === b.elevationReference &&
      a.elevationTolerance === b.elevationTolerance &&
      a.elevationOffset === b.elevationOffset &&
      a.elevationBandCount === b.elevationBandCount
    );
  }
}
